/**
 * 高度な売上分析（統合版）
 * V1の異常値検出・カテゴリ円グラフ + V2のページネーション・表示切替
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  TrendingUp, TrendingDown, Sun, CloudRain,
  Filter, ArrowUpDown,
  DollarSign, Users, Award, AlertCircle,
  ChevronDown, ChevronUp, Download, Sparkles,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  Eye, Table, BarChart3, Calendar
} from 'lucide-react';
import {
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Area, AreaChart
} from 'recharts';
import { format, parseISO, getDay } from 'date-fns';
import { ja } from 'date-fns/locale';

import { Lock, Crown } from 'lucide-react';
import PageContainer from '../layout/PageContainer';

const isUnlocked = false;

interface DailySalesData {
  date: string;
  dayOfWeek: string;
  isWeekend: boolean;
  isHoliday: boolean;
  weather: string;
  temp_high: number;
  temp_low: number;
  temp_avg: number;
  total_sales: number;
  cash_sales: number;
  paypay_sales: number;
  customer_count: number;
  bento_sales: number;
  teishoku_sales: number;
  side_sales: number;
  drink_sales: number;
  ranking?: number;
}

interface FilterOptions {
  dateRange: {
    start: string;
    end: string;
    preset: 'custom' | 'week' | 'month' | 'quarter' | 'year' | '3years';
  };
  weather: string[];
  temperature: {
    min: number | null;
    max: number | null;
  };
  dayType: 'all' | 'weekday' | 'weekend' | 'holiday' | 'specific';
  specificDays: number[];
  sortBy: 'date' | 'sales' | 'customers' | 'temp';
  sortOrder: 'asc' | 'desc';
}

const AdvancedSalesAnalysisV2: React.FC = () => {
  const [salesData, setSalesData] = useState<DailySalesData[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'graph' | 'both'>('both');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [filters, setFilters] = useState<FilterOptions>({
    dateRange: {
      start: '',
      end: '',
      preset: 'month'
    },
    weather: [],
    temperature: {
      min: null,
      max: null
    },
    dayType: 'all',
    specificDays: [],
    sortBy: 'date',
    sortOrder: 'desc'
  });

  useEffect(() => {
    if (isUnlocked) {
      fetchRealData();
    }
  }, [filters.dateRange.preset]);

  const fetchRealData = async () => {
    try {
      let days = 30;
      switch(filters.dateRange.preset) {
        case 'week': days = 7; break;
        case 'month': days = 30; break;
        case 'quarter': days = 90; break;
        case 'year': days = 365; break;
        case '3years': days = 365 * 3; break;
      }

      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - (days - 1));

      const start = format(startDate, 'yyyy-MM-dd');
      const end = format(endDate, 'yyyy-MM-dd');

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}/advanced-analytics/daily-sales?start=${start}&end=${end}`
      );

      if (!response.ok) {
        throw new Error('データの取得に失敗しました');
      }

      const result = await response.json();
      setSalesData(result.data || []);
      setCurrentPage(1);
    } catch (error) {
      console.error('実データ取得エラー:', error);
      setSalesData([]);
      setCurrentPage(1);
    }
  };

  // フィルタリングとソート処理
  const filteredAndSortedData = useMemo(() => {
    let filtered = [...salesData];

    if (filters.dateRange.start && filters.dateRange.end) {
      filtered = filtered.filter(d =>
        d.date >= filters.dateRange.start && d.date <= filters.dateRange.end
      );
    }

    if (filters.weather.length > 0) {
      filtered = filtered.filter(d => filters.weather.includes(d.weather));
    }

    if (filters.temperature.min !== null) {
      filtered = filtered.filter(d => d.temp_avg >= filters.temperature.min!);
    }
    if (filters.temperature.max !== null) {
      filtered = filtered.filter(d => d.temp_avg <= filters.temperature.max!);
    }

    switch (filters.dayType) {
      case 'weekday':
        filtered = filtered.filter(d => !d.isWeekend && !d.isHoliday);
        break;
      case 'weekend':
        filtered = filtered.filter(d => d.isWeekend);
        break;
      case 'holiday':
        filtered = filtered.filter(d => d.isHoliday);
        break;
      case 'specific':
        if (filters.specificDays.length > 0) {
          filtered = filtered.filter(d => {
            const day = getDay(parseISO(d.date));
            return filters.specificDays.includes(day);
          });
        }
        break;
    }

    filtered.sort((a, b) => {
      let compareValue = 0;
      switch (filters.sortBy) {
        case 'date':
          compareValue = a.date.localeCompare(b.date);
          break;
        case 'sales':
          compareValue = a.total_sales - b.total_sales;
          break;
        case 'customers':
          compareValue = a.customer_count - b.customer_count;
          break;
        case 'temp':
          compareValue = a.temp_avg - b.temp_avg;
          break;
      }
      return filters.sortOrder === 'asc' ? compareValue : -compareValue;
    });

    return filtered.map((item, index) => ({
      ...item,
      ranking: index + 1
    }));
  }, [salesData, filters]);

  // ページネーション
  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageData = filteredAndSortedData.slice(startIndex, endIndex);

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxButtons = 10;
    let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start < maxButtons - 1) {
      start = Math.max(1, end - maxButtons + 1);
    }
    for (let i = start; i <= end; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // 統計情報
  const statistics = useMemo(() => {
    if (filteredAndSortedData.length === 0) {
      return {
        avgSales: 0, maxSales: 0, minSales: 0, totalSales: 0,
        avgCustomers: 0, bestDay: null as DailySalesData | null, worstDay: null as DailySalesData | null,
        weatherImpact: {} as Record<string, number>
      };
    }

    const totalSales = filteredAndSortedData.reduce((sum, d) => sum + d.total_sales, 0);
    const avgSales = totalSales / filteredAndSortedData.length;
    const sortedBySales = [...filteredAndSortedData].sort((a, b) => b.total_sales - a.total_sales);

    const weatherGroups = filteredAndSortedData.reduce((acc, d) => {
      if (!acc[d.weather]) acc[d.weather] = { total: 0, count: 0 };
      acc[d.weather].total += d.total_sales;
      acc[d.weather].count++;
      return acc;
    }, {} as Record<string, { total: number; count: number }>);

    const weatherImpact = Object.entries(weatherGroups).reduce((acc, [weather, data]) => {
      acc[weather] = Math.round(data.total / data.count);
      return acc;
    }, {} as Record<string, number>);

    return {
      avgSales: Math.round(avgSales),
      maxSales: sortedBySales[0]?.total_sales || 0,
      minSales: sortedBySales[sortedBySales.length - 1]?.total_sales || 0,
      totalSales,
      avgCustomers: Math.round(
        filteredAndSortedData.reduce((sum, d) => sum + d.customer_count, 0) /
        filteredAndSortedData.length
      ),
      bestDay: sortedBySales[0],
      worstDay: sortedBySales[sortedBySales.length - 1],
      weatherImpact
    };
  }, [filteredAndSortedData]);

  // 異常値検出（V1から統合）
  const anomalies = useMemo(() => {
    if (filteredAndSortedData.length === 0) return [];
    const mean = statistics.avgSales;
    const stdDev = Math.sqrt(
      filteredAndSortedData.reduce((sum, d) =>
        sum + Math.pow(d.total_sales - mean, 2), 0
      ) / filteredAndSortedData.length
    );
    return filteredAndSortedData.filter(d =>
      Math.abs(d.total_sales - mean) > stdDev * 2
    );
  }, [filteredAndSortedData, statistics]);

  // カテゴリ別売上（V1から統合）
  const categoryData = useMemo(() => {
    const totals = filteredAndSortedData.reduce((acc, d) => {
      acc.bento += d.bento_sales;
      acc.teishoku += d.teishoku_sales;
      acc.side += d.side_sales;
      acc.drink += d.drink_sales;
      return acc;
    }, { bento: 0, teishoku: 0, side: 0, drink: 0 });

    return [
      { name: '弁当', value: totals.bento, color: '#3B82F6' },
      { name: '定食', value: totals.teishoku, color: '#10B981' },
      { name: 'サイド', value: totals.side, color: '#F59E0B' },
      { name: 'ドリンク', value: totals.drink, color: '#8B5CF6' }
    ];
  }, [filteredAndSortedData]);

  const handleExport = () => {
    const csvContent = [
      ['日付', '曜日', '天候', '最高気温', '最低気温', '売上', '客数', '弁当', '定食', 'サイド', 'ドリンク'],
      ...filteredAndSortedData.map(d => [
        d.date, d.dayOfWeek, d.weather, d.temp_high, d.temp_low,
        d.total_sales, d.customer_count,
        d.bento_sales, d.teishoku_sales, d.side_sales, d.drink_sales
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `sales_analysis_${format(new Date(), 'yyyyMMdd')}.csv`;
    link.click();
  };

  if (!isUnlocked) {
    return (
      <div className="p-6">
        {/* Premium Overlay */}
        <div style={{
          background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
          borderRadius: '16px',
          padding: '48px 32px',
          color: 'white',
          textAlign: 'center',
          marginBottom: '24px',
          boxShadow: '0 20px 60px rgba(250, 112, 154, 0.3)'
        }}>
          <Crown style={{ width: '64px', height: '64px', marginBottom: '16px', color: '#ffd700' }} className="mx-auto" />
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '8px', color: 'white' }}>
            高度な分析
          </h1>
          <p style={{ fontSize: '16px', opacity: 0.9, marginBottom: '32px' }}>
            天候データと売上データを組み合わせた高度な分析機能です
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {[
              { icon: <Filter className="w-6 h-6" />, title: '高度なフィルタリング', desc: '天候・曜日・気温で絞り込み' },
              { icon: <ArrowUpDown className="w-6 h-6" />, title: 'ソート・ページネーション', desc: '売上・客数・気温で並べ替え' },
              { icon: <BarChart3 className="w-6 h-6" />, title: '天候別グラフ', desc: '天候と売上の相関を可視化' },
              { icon: <Download className="w-6 h-6" />, title: 'CSV出力', desc: '分析データをエクスポート' },
            ].map((item, idx) => (
              <div key={idx} style={{
                background: 'rgba(255,255,255,0.15)',
                borderRadius: '12px',
                padding: '16px',
                backdropFilter: 'blur(10px)'
              }}>
                <div style={{ marginBottom: '8px' }} className="flex justify-center">{item.icon}</div>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{item.title}</div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>{item.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Blurred Preview */}
        <div style={{ position: 'relative', overflow: 'hidden', borderRadius: '12px' }}>
          <div style={{ filter: 'blur(8px)', pointerEvents: 'none', opacity: 0.5 }}>
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h1 className="text-3xl font-bold text-gray-800 mb-4">高度な売上分析</h1>
              <div style={{ height: '300px', background: '#f5f5f5', borderRadius: '8px' }} />
            </div>
          </div>
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center'
          }}>
            <Lock style={{ width: '48px', height: '48px', color: '#fa709a' }} className="mx-auto" />
            <p style={{ color: '#fa709a', fontWeight: 'bold', marginTop: '8px' }}>プレミアム機能</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PageContainer>
      {/* ヘッダー */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            <TrendingUp className="w-8 h-8 text-blue-600" />
            高度な売上分析
          </h1>
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Filter className="w-4 h-4" />
              絞り込み
              {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              CSV出力
            </button>
          </div>
        </div>

        {/* 統計サマリー */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-6 h-6 text-blue-600" />
              <span className="text-sm text-gray-600">平均売上</span>
            </div>
            <div className="text-2xl font-bold">&yen;{statistics.avgSales.toLocaleString()}</div>
            <div className="text-xs text-gray-500">{filteredAndSortedData.length}日間</div>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-6 h-6 text-green-600" />
              <span className="text-sm text-gray-600">最高売上</span>
            </div>
            <div className="text-2xl font-bold">&yen;{statistics.maxSales.toLocaleString()}</div>
            {statistics.bestDay && (
              <div className="text-xs text-gray-500">
                {statistics.bestDay.date} ({statistics.bestDay.weather})
              </div>
            )}
          </div>

          <div className="bg-gradient-to-r from-red-50 to-red-100 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <TrendingDown className="w-6 h-6 text-red-600" />
              <span className="text-sm text-gray-600">最低売上</span>
            </div>
            <div className="text-2xl font-bold">&yen;{statistics.minSales.toLocaleString()}</div>
            {statistics.worstDay && (
              <div className="text-xs text-gray-500">
                {statistics.worstDay.date} ({statistics.worstDay.weather})
              </div>
            )}
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-6 h-6 text-purple-600" />
              <span className="text-sm text-gray-600">平均客数</span>
            </div>
            <div className="text-2xl font-bold">{statistics.avgCustomers}名</div>
            <div className="text-xs text-gray-500">
              客単価: &yen;{statistics.avgCustomers > 0 ? Math.round(statistics.avgSales / statistics.avgCustomers).toLocaleString() : 0}
            </div>
          </div>

          <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <AlertCircle className="w-6 h-6 text-orange-600" />
              <span className="text-sm text-gray-600">異常値</span>
            </div>
            <div className="text-2xl font-bold">{anomalies.length}日</div>
            <div className="text-xs text-gray-500">標準偏差の2倍超</div>
          </div>
        </div>
      </div>

      {/* フィルターパネル */}
      {showFilters && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">絞り込み条件</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* 期間選択 */}
            <div>
              <label className="block text-sm font-medium mb-2">期間</label>
              <select
                value={filters.dateRange.preset}
                onChange={(e) => setFilters({
                  ...filters,
                  dateRange: { ...filters.dateRange, preset: e.target.value as any }
                })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="week">1週間</option>
                <option value="month">1ヶ月</option>
                <option value="quarter">3ヶ月</option>
                <option value="year">1年</option>
                <option value="3years">3年</option>
                <option value="custom">カスタム</option>
              </select>
              {filters.dateRange.preset === 'custom' && (
                <div className="mt-2 space-y-2">
                  <input
                    type="date"
                    value={filters.dateRange.start}
                    onChange={(e) => setFilters({
                      ...filters,
                      dateRange: { ...filters.dateRange, start: e.target.value }
                    })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <input
                    type="date"
                    value={filters.dateRange.end}
                    onChange={(e) => setFilters({
                      ...filters,
                      dateRange: { ...filters.dateRange, end: e.target.value }
                    })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              )}
            </div>

            {/* 天候フィルタ */}
            <div>
              <label className="block text-sm font-medium mb-2">天候</label>
              <div className="space-y-2">
                {['晴れ', '曇り', '雨', '雪'].map(weather => (
                  <label key={weather} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.weather.includes(weather)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFilters({ ...filters, weather: [...filters.weather, weather] });
                        } else {
                          setFilters({ ...filters, weather: filters.weather.filter(w => w !== weather) });
                        }
                      }}
                      className="mr-2"
                    />
                    <span className="flex items-center gap-1">
                      {weather === '晴れ' && <Sun className="w-4 h-4 text-yellow-500" />}
                      {weather === '曇り' && <CloudRain className="w-4 h-4 text-gray-500" />}
                      {weather === '雨' && <CloudRain className="w-4 h-4 text-blue-500" />}
                      {weather}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* 曜日フィルタ */}
            <div>
              <label className="block text-sm font-medium mb-2">曜日タイプ</label>
              <select
                value={filters.dayType}
                onChange={(e) => setFilters({ ...filters, dayType: e.target.value as any })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="all">すべて</option>
                <option value="weekday">平日のみ</option>
                <option value="weekend">週末のみ</option>
                <option value="holiday">祝日のみ</option>
                <option value="specific">曜日指定</option>
              </select>
              {filters.dayType === 'specific' && (
                <div className="mt-2 grid grid-cols-7 gap-1">
                  {['日', '月', '火', '水', '木', '金', '土'].map((day, index) => (
                    <button
                      key={day}
                      onClick={() => {
                        if (filters.specificDays.includes(index)) {
                          setFilters({ ...filters, specificDays: filters.specificDays.filter(d => d !== index) });
                        } else {
                          setFilters({ ...filters, specificDays: [...filters.specificDays, index] });
                        }
                      }}
                      className={`px-2 py-1 text-xs rounded ${
                        filters.specificDays.includes(index) ? 'bg-blue-600 text-white' : 'bg-gray-200'
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ソート設定 */}
            <div>
              <label className="block text-sm font-medium mb-2">
                <ArrowUpDown className="inline w-4 h-4 mr-1" />
                並び替え
              </label>
              <select
                value={filters.sortBy}
                onChange={(e) => setFilters({ ...filters, sortBy: e.target.value as any })}
                className="w-full px-3 py-2 border rounded-lg mb-2"
              >
                <option value="date">日付</option>
                <option value="sales">売上</option>
                <option value="customers">客数</option>
                <option value="temp">気温</option>
              </select>
              <select
                value={filters.sortOrder}
                onChange={(e) => setFilters({ ...filters, sortOrder: e.target.value as any })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="desc">降順（高→低）</option>
                <option value="asc">昇順（低→高）</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* 表示モード切替 */}
      <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('table')}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                viewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-gray-200'
              }`}
            >
              <Table className="w-4 h-4" />
              テーブル
            </button>
            <button
              onClick={() => setViewMode('graph')}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                viewMode === 'graph' ? 'bg-blue-600 text-white' : 'bg-gray-200'
              }`}
            >
              <BarChart3 className="w-4 h-4" />
              グラフ
            </button>
            <button
              onClick={() => setViewMode('both')}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                viewMode === 'both' ? 'bg-blue-600 text-white' : 'bg-gray-200'
              }`}
            >
              <Eye className="w-4 h-4" />
              両方
            </button>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">表示件数:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              className="px-3 py-1 border rounded-lg"
            >
              <option value="10">10件</option>
              <option value="20">20件</option>
              <option value="50">50件</option>
              <option value="100">100件</option>
              <option value="200">200件</option>
            </select>
          </div>
        </div>
      </div>

      {/* グラフエリア */}
      {(viewMode === 'graph' || viewMode === 'both') && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* 売上推移 */}
          <div className="bg-white rounded-lg shadow-lg p-6 lg:col-span-2">
            <h3 className="text-lg font-semibold mb-4">売上推移（直近30日）</h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={filteredAndSortedData.slice(0, 30)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(date) => format(parseISO(date), 'MM/dd')}
                />
                <YAxis />
                <Tooltip
                  labelFormatter={(date) => format(parseISO(date as string), 'yyyy/MM/dd (E)', { locale: ja })}
                  formatter={(value: any) => `\u00a5${value.toLocaleString()}`}
                />
                <Area
                  type="monotone"
                  dataKey="total_sales"
                  stroke="#3B82F6"
                  fill="#3B82F6"
                  fillOpacity={0.3}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* カテゴリ別売上（V1から統合） */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold mb-4">カテゴリ別売上</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => percent > 0.01 ? `${name} ${(percent * 100).toFixed(0)}%` : ''}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {categoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: any) => `\u00a5${value.toLocaleString()}`} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* 天候別平均売上 */}
          <div className="bg-white rounded-lg shadow-lg p-6 lg:col-span-2">
            <h3 className="text-lg font-semibold mb-4">天候別平均売上</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={Object.entries(statistics.weatherImpact).map(([weather, sales]) => ({
                weather, sales
              }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="weather" />
                <YAxis />
                <Tooltip formatter={(value: any) => `\u00a5${value.toLocaleString()}`} />
                <Bar dataKey="sales" fill="#10B981" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* カテゴリ別金額テーブル */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold mb-4">カテゴリ別合計</h3>
            <div className="space-y-3">
              {categoryData.map((cat) => (
                <div key={cat.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                    <span className="text-sm font-medium">{cat.name}</span>
                  </div>
                  <span className="text-sm font-bold">&yen;{cat.value.toLocaleString()}</span>
                </div>
              ))}
              <div className="border-t pt-2 flex items-center justify-between">
                <span className="text-sm font-bold">合計</span>
                <span className="text-sm font-bold">&yen;{categoryData.reduce((s, c) => s + c.value, 0).toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* データテーブル */}
      {(viewMode === 'table' || viewMode === 'both') && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">
              詳細データ（{startIndex + 1}〜{Math.min(endIndex, filteredAndSortedData.length)}件 / 全{filteredAndSortedData.length}件）
              {anomalies.length > 0 && (
                <span className="ml-4 text-sm text-orange-600">
                  <AlertCircle className="inline w-4 h-4 mr-1" />
                  {anomalies.length}件の異常値を検出
                </span>
              )}
            </h3>

            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed">
                <ChevronsLeft className="w-4 h-4" />
              </button>
              <button onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600">{currentPage} / {totalPages} ページ</span>
              <button onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed">
                <ChevronRight className="w-4 h-4" />
              </button>
              <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages || totalPages === 0}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed">
                <ChevronsRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="px-4 py-2 text-left">順位</th>
                  <th className="px-4 py-2 text-left">日付</th>
                  <th className="px-4 py-2 text-center">曜日</th>
                  <th className="px-4 py-2 text-center">天候</th>
                  <th className="px-4 py-2 text-right">気温</th>
                  <th className="px-4 py-2 text-right">売上</th>
                  <th className="px-4 py-2 text-right">客数</th>
                  <th className="px-4 py-2 text-right">客単価</th>
                  <th className="px-4 py-2 text-right">弁当</th>
                  <th className="px-4 py-2 text-right">定食</th>
                  <th className="px-4 py-2 text-center">特記</th>
                </tr>
              </thead>
              <tbody>
                {currentPageData.map((day, index) => {
                  const isAnomaly = anomalies.some(a => a.date === day.date);
                  const avgPrice = day.customer_count > 0 ? Math.round(day.total_sales / day.customer_count) : 0;
                  const globalRanking = startIndex + index + 1;

                  return (
                    <tr
                      key={day.date}
                      className={`border-b hover:bg-gray-50 ${isAnomaly ? 'bg-orange-50' : ''}`}
                    >
                      <td className="px-4 py-2">
                        {filters.sortBy === 'sales' && globalRanking <= 3 && (
                          <>
                            {globalRanking === 1 && <Award className="inline w-4 h-4 text-yellow-500 mr-1" />}
                            {globalRanking === 2 && <Award className="inline w-4 h-4 text-gray-400 mr-1" />}
                            {globalRanking === 3 && <Award className="inline w-4 h-4 text-orange-600 mr-1" />}
                          </>
                        )}
                        {globalRanking}
                      </td>
                      <td className="px-4 py-2">{day.date}</td>
                      <td className="px-4 py-2 text-center">
                        <span className={`px-2 py-1 rounded text-xs ${
                          day.isWeekend ? 'bg-blue-100 text-blue-800' :
                          day.isHoliday ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {day.dayOfWeek}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center">
                        {day.weather === '晴れ' && <Sun className="inline w-4 h-4 text-yellow-500" />}
                        {day.weather === '曇り' && <CloudRain className="inline w-4 h-4 text-gray-500" />}
                        {day.weather === '雨' && <CloudRain className="inline w-4 h-4 text-blue-500" />}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {day.temp_low}〜{day.temp_high}℃
                      </td>
                      <td className="px-4 py-2 text-right font-semibold">
                        &yen;{day.total_sales.toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-right">{day.customer_count}名</td>
                      <td className="px-4 py-2 text-right">&yen;{avgPrice.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right text-sm text-gray-600">
                        &yen;{day.bento_sales.toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-right text-sm text-gray-600">
                        &yen;{day.teishoku_sales.toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-center">
                        {isAnomaly && (
                          <span className="text-orange-600" title="異常値">
                            <AlertCircle className="inline w-4 h-4" />
                          </span>
                        )}
                        {day.total_sales > statistics.avgSales * 1.2 && (
                          <span className="text-green-600" title="好調">
                            <Sparkles className="inline w-4 h-4" />
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ページネーション（下部） */}
          {totalPages > 1 && (
            <div className="mt-4 flex justify-center items-center gap-2">
              <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}
                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed">
                最初
              </button>
              {currentPage > 3 && <span className="text-gray-500">...</span>}
              {getPageNumbers().map(pageNumber => (
                <button
                  key={pageNumber}
                  onClick={() => setCurrentPage(pageNumber)}
                  className={`px-3 py-1 rounded ${
                    currentPage === pageNumber ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'
                  }`}
                >
                  {pageNumber}
                </button>
              ))}
              {currentPage < totalPages - 2 && <span className="text-gray-500">...</span>}
              <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}
                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed">
                最後
              </button>
            </div>
          )}
        </div>
      )}
    </PageContainer>
  );
};

export default AdvancedSalesAnalysisV2;
